// Need value import for enum usage
import { useQuery } from "@tanstack/react-query";
import lodashIsNill from "lodash/fp/isNil";
import { content_type } from "prisma-generated/enums";
import { useMemo } from "react";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyAppRouteConfigContentPageIdContract } from "shared/api/contracts/society/societyId/appRouteConfig/appRouteConfigId/content/contentPageId";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type ContentPageView } from "shared/mappers/database/content-page/content-page";
import { type StorePageRouteParams } from "shared/types/HubPageRouteParams";
import LoadingPage from "hub/src/components/common/LoadingPage";
import HubDocumentsTable from "hub/src/components/documents/HubDocumentsTable";
import { ProductGrid } from "hub/src/components/store/ProductGrid";
import { HubCheckoutOptionalProductGrid } from "hub/src/pages/checkout/optional-products/HubCheckoutOptionalProductGrid";

type ContentZoneParamsUnion = StorePageRouteParams;

export type ContentZoneProps = {
  contentPages: ContentPageView[];
  zone: string;
  appRouteConfigId: number;
  className?: string;
  testId?: string;
  params?: ContentZoneParamsUnion;
  additionalInfo?: Record<string, any>;
};

const ContentZone = ({
  contentPages,
  zone,
  appRouteConfigId,
  className,
  testId,
  params,
  additionalInfo,
}: ContentZoneProps) => {
  const session = useCustomSession();

  //Find the contentPages that matches the zone with the highest priority
  const contentPage = useMemo(() => {
    return (
      contentPages
        ?.filter((contentPage) => contentPage.zone === zone)
        .sort((a, b) => a.priority - b.priority) ?? []
    );
  }, [contentPages, zone]);

  const societyId = session?.societyId;
  const contentPageId = contentPage?.[0]?.id;

  const { data: content } = useQuery({
    queryKey: ["contentZone", societyId, appRouteConfigId, contentPageId],
    queryFn: async () => {
      if (!societyId || lodashIsNill(contentPageId)) {
        return {
          contentType: content_type.html,
          content: "",
          description: "",
          id: 0,
          data: {},
          zone: "",
          priority: 0,
        };
      }

      return apiRequest({
        contract: getSocietyAppRouteConfigContentPageIdContract,
        inputs: {
          params: {
            societyId,
            appRouteConfigId,
            contentPageId,
          },
        },
      });
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  if (!content) {
    return <LoadingPage />;
  }

  if (content.contentType === content_type.file) {
    return (
      <HubDocumentsTable
        title={content.description}
        documents={content?.data.documents ?? []}
      />
    );
  }

  if (content.contentType === content_type.product_grid) {
    return (
      <ProductGrid
        gridId={content.id}
        params={params}
        title={content.description}
        products={content?.data?.products ?? []}
      />
    );
  }

  //TODO:  this pattern is getting grosser and grosser every time we do it.
  // we should clean it up later as this is just adding to tech debt ideally the
  // checkout page would have its own component that is expecting the checkout
  // optional product content type as it should never be used anywhere but a checkout
  // page and doing this risks that somehow happening

  if (content.contentType === content_type.checkout_optional_products) {
    return (
      <HubCheckoutOptionalProductGrid
        title={content.description}
        products={content?.data.products ?? []}
      />
    );
  }

  if (content.contentType === content_type.html && content.content === "") {
    return null;
  }
  return (
    <TemplateComponent
      className={className}
      template={content.content}
      testid={testId}
      data={{
        ...session,
        additionalInfo,
        pageData: content.data,
      }}
    />
  );
};

export default ContentZone;
